import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import purchaseOrderService from '../services/purchaseOrders';
import sprayService from '../services/spray';

interface PurchaseOrderAccessResult {
  /** Authorised by the organisation's admin to raise, send and receive orders. */
  canRaise: boolean;
  /** Authorised, or holding an Accounts seat. */
  canRead: boolean;
  loading: boolean;
}

/**
 * What this person can do with purchase orders in the organisation the tab is
 * showing. The answer is the server's (`GET /spray/purchase-orders/access`):
 * the organisation's admin authorises people on the Organisation Admin page,
 * and no licence seat decides it. A failed check reads as no access; the
 * server refuses regardless.
 */
export function usePurchaseOrderAccess(): PurchaseOrderAccessResult {
  const { user } = useAuth();
  const [state, setState] = useState<PurchaseOrderAccessResult>({ canRaise: false, canRead: false, loading: true });

  useEffect(() => {
    let cancelled = false;
    const done = (canRaise: boolean, canRead: boolean) => {
      if (!cancelled) setState({ canRaise, canRead, loading: false });
    };
    if (!user?.organisation_id) {
      done(false, false);
    } else if (sprayService.getTargetOrgId() !== null) {
      // A spray advisor acting for another organisation: that organisation's
      // advisor record decides, on each request.
      done(true, true);
    } else {
      purchaseOrderService.getAccess()
        .then((res) => done(Boolean(res.success && res.data?.can_raise), Boolean(res.success && res.data?.can_read)))
        .catch(() => done(false, false));
    }
    return () => { cancelled = true; };
  }, [user?.organisation_id]);

  return state;
}
